import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { extractApiErrorMessage } from '../../../../utils/apiError'
import { getTaskMasterDropdown } from '../../task-master/api/taskMasterApi'
import {
  archiveJobMaster,
  unarchiveJobMaster,
  createJobMaster,
  getJobMasterDetail,
  updateJobMaster,
} from '../api/jobMasterApi'
import { submitMasterChange } from '../../../approval/masters/api/masterApprovalApi'
import { appToast } from '../../../../components/AppToast'

function normalizeText(value) {
  const trimmed = String(value || '').trim()
  return trimmed.length > 0 ? trimmed : undefined
}

function normalizeNullableNumber(value) {
  const text = String(value || '').trim()
  if (!text) return null

  const parsed = Number(text)
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null
}

export function useJobMasterEntryScreen() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const editingJobMasterId = searchParams.get('id')

  const savingRef = useRef(false)
  const preselectedTaskMasterId = searchParams.get('taskMasterId') || ''

  const [form, setForm] = useState({
    job_type_name: '',
    parent_task_master_id: preselectedTaskMasterId,
    effort_hours: '0',
    effort_minutes: '30',
    sequence_order: '0',
    sla_hours: '',
  })
  const [operationalStatus, setOperationalStatus] = useState('active')

  const [taskMasterOptions, setTaskMasterOptions] = useState([])
  const [isLoading, setIsLoading] = useState(Boolean(editingJobMasterId))
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')

  useEffect(() => {
    let active = true

    async function loadTaskMasterOptions() {
      try {
        const options = await getTaskMasterDropdown()
        if (!active) return
        setTaskMasterOptions(options)
      } catch {
        if (!active) return
        setTaskMasterOptions([])
      }
    }

    loadTaskMasterOptions()

    return () => {
      active = false
    }
  }, [])

  useEffect(() => {
    let active = true

    async function loadIfEditing() {
      if (!editingJobMasterId) return
      setIsLoading(true)
      setError('')

      try {
        const detail = await getJobMasterDetail(editingJobMasterId)
        if (!active) return

        const totalHours = parseFloat(detail?.default_estimated_hours ?? 0.5)
        const hours = Math.floor(totalHours)
        const minutes = Math.round((totalHours - hours) * 60)

        setForm({
          job_type_name: detail?.job_type_name || '',
          parent_task_master_id: detail?.parent_task_master_id ? String(detail.parent_task_master_id) : '',
          effort_hours: String(hours),
          effort_minutes: String(minutes),
          sequence_order: String(detail?.sequence_order ?? '0'),
          sla_hours: detail?.sla_hours ? String(detail.sla_hours) : '',
        })
        setOperationalStatus(Number(detail?.is_archive) === 1 ? 'inactive' : 'active')
      } catch (requestError) {
        if (!active) return
        setError(extractApiErrorMessage(requestError, 'Failed to load job master details.'))
      } finally {
        if (active) setIsLoading(false)
      }
    }

    loadIfEditing()

    return () => {
      active = false
    }
  }, [editingJobMasterId])

  const completionPercent = useMemo(() => {
    const trackedFields = ['job_type_name', 'parent_task_master_id', 'effort_hours', 'effort_minutes', 'sequence_order']
    const filledCount = trackedFields.filter((key) => String(form[key] || '').trim().length > 0).length
    const optionalFilled = String(form.sla_hours || '').trim().length > 0 ? 1 : 0
    return Math.round(((filledCount + optionalFilled) / (trackedFields.length + 1)) * 100)
  }, [form])

  function patchForm(key, value) {
    setForm((prev) => ({ ...prev, [key]: value }))
  }

  function buildPayload() {
    const h = parseInt(form.effort_hours || 0, 10)
    const m = parseInt(form.effort_minutes || 0, 10)
    const decimalHours = h + m / 60

    return {
      job_type_name: normalizeText(form.job_type_name),
      parent_task_master_id: Number(form.parent_task_master_id),
      default_estimated_hours: decimalHours || 0.1,
      sequence_order: Math.max(0, Math.floor(Number(form.sequence_order) || 0)),
      sla_hours: normalizeNullableNumber(form.sla_hours),
      is_archive: operationalStatus === 'inactive' ? 1 : 0,
    }
  }

  async function handleSave() {
    if (savingRef.current) return
    savingRef.current = true
    setError('')
    setSuccess('')

    const cleanName = String(form.job_type_name || '').trim()
    if (!cleanName) {
      setError('Job Type Name is required.')
      savingRef.current = false
      return
    }
    if (cleanName.length < 5) {
      setError('Job Type Name must be at least 5 characters.')
      savingRef.current = false
      return
    }

    if (!form.parent_task_master_id) {
      setError('Parent Task Master blueprint is required.')
      savingRef.current = false
      return
    }

    const h = parseInt(form.effort_hours || 0, 10)
    const m = parseInt(form.effort_minutes || 0, 10)
    if (Number.isNaN(h) || Number.isNaN(m) || h < 0 || m < 0 || m >= 60 || (h === 0 && m === 0)) {
      setError('Default estimated hours must be greater than zero, and minutes must be between 0 and 59.')
      savingRef.current = false
      return
    }

    const seqOrder = Number(form.sequence_order)
    if (Number.isNaN(seqOrder) || seqOrder < 0) {
      setError('Sequence Order must be a non-negative integer.')
      savingRef.current = false
      return
    }

    if (form.sla_hours) {
      const slaHoursVal = Number(form.sla_hours)
      if (Number.isNaN(slaHoursVal) || slaHoursVal < 1) {
        setError('SLA Hours override must be a positive integer of at least 1.')
        savingRef.current = false
        return
      }
    }

    setIsSaving(true)

    try {
      const payload = buildPayload()
      const action = editingJobMasterId ? 'UPDATE' : 'CREATE'

      const result = await submitMasterChange({
        module_key: 'job_master',
        action,
        entity_id: editingJobMasterId ? Number(editingJobMasterId) : undefined,
        payload,
      })

      const msg = result?.message || (editingJobMasterId
        ? 'Job Master update submitted for approval.'
        : 'Job Master creation submitted for approval.')
      appToast.success(msg)
      setPendingApproval({
        requestId: result?.approval_request_id,
        message: msg,
      })
      setSuccess(msg)
      savingRef.current = false
      setIsSaving(false)
      navigate('/masters/job-master/list')
    } catch (requestError) {
      setError(extractApiErrorMessage(requestError, 'Failed to submit job master change for approval.'))
      savingRef.current = false
      setIsSaving(false)
    }
  }

  return {
    state: {
      form,
      operationalStatus,
      completionPercent,
      taskMasterOptions,
      isLoading,
      isSaving,
      error,
      success,
      pendingApproval,
      isEditMode: Boolean(editingJobMasterId),
    },
    actions: {
      patchForm,
      setOperationalStatus,
      handleSave,
    },
  }
}
